import { useState } from 'react';
import useSWR from 'swr';
import { toast } from 'App';
import {
  CreatePermissionGate,
  UpdatePermissionGate,
} from 'components/PermissionGate';
import { PERMISSION_RESOURCES } from 'constants/permissions';
import { adminService } from 'views/services/adminService';
import { invoicePaymentsService } from 'views/services/invoicePaymentsService';
import {
  EConfigStatus,
  IInvoiceSender,
  IInvoiceSenderAccount,
} from 'views/types/invoicePayments';
import {
  cellClass,
  csvToArrayOrNull,
  emptyToNull,
  headerClass,
  inputCls,
  labelCls,
  primaryBtn,
  secondaryBtn,
  smallBtn,
} from './shared';

interface ISenderDraft {
  name: string;
  country: string;
  address: string;
  registration_number: string;
  website_url: string;
  supported_sector_categories: string;
  supported_beneficiary_countries: string;
  supported_bank_countries: string;
  min_amount: string;
  max_amount: string;
  internal_notes: string;
}

const EMPTY_SENDER: ISenderDraft = {
  name: '',
  country: '',
  address: '',
  registration_number: '',
  website_url: '',
  supported_sector_categories: '',
  supported_beneficiary_countries: '',
  supported_bank_countries: '',
  min_amount: '',
  max_amount: '',
  internal_notes: '',
};

interface IAccountDraft {
  currency: string;
  bank_name: string;
  bank_country: string;
  bank_address: string;
  iban: string;
  account_number: string;
  sort_code: string;
  routing_number: string;
  bic_swift: string;
  supported_rails: string;
}

const EMPTY_ACCOUNT: IAccountDraft = {
  currency: '',
  bank_name: '',
  bank_country: '',
  bank_address: '',
  iban: '',
  account_number: '',
  sort_code: '',
  routing_number: '',
  bic_swift: '',
  supported_rails: '',
};

const senderToDraft = (sender: IInvoiceSender): ISenderDraft => ({
  name: sender.name,
  country: sender.country,
  address: sender.address,
  registration_number: sender.registration_number,
  website_url: sender.website_url ?? '',
  supported_sector_categories: (sender.supported_sector_categories ?? []).join(
    ', '
  ),
  supported_beneficiary_countries: (
    sender.supported_beneficiary_countries ?? []
  ).join(', '),
  supported_bank_countries: (sender.supported_bank_countries ?? []).join(', '),
  min_amount: sender.min_amount ?? '',
  max_amount: sender.max_amount ?? '',
  internal_notes: sender.internal_notes ?? '',
});

const accountToDraft = (account: IInvoiceSenderAccount): IAccountDraft => ({
  currency: account.currency,
  bank_name: account.bank_name,
  bank_country: account.bank_country,
  bank_address: account.bank_address,
  iban: account.iban ?? '',
  account_number: account.account_number ?? '',
  sort_code: account.sort_code ?? '',
  routing_number: account.routing_number ?? '',
  bic_swift: account.bic_swift ?? '',
  supported_rails: (account.supported_rails ?? []).join(', '),
});

export const SendersPanel = () => {
  const [busy, setBusy] = useState(false);
  const [senderFormOpen, setSenderFormOpen] = useState(false);
  const [editingSenderId, setEditingSenderId] = useState<string | null>(null);
  const [senderDraft, setSenderDraft] = useState<ISenderDraft>(EMPTY_SENDER);
  const [accountFormSenderId, setAccountFormSenderId] = useState<string | null>(
    null
  );
  const [editingAccountId, setEditingAccountId] = useState<string | null>(null);
  const [accountDraft, setAccountDraft] =
    useState<IAccountDraft>(EMPTY_ACCOUNT);

  const { data: senders, mutate } = useSWR(
    '/api/admin/invoice-payment-config/senders',
    () => invoicePaymentsService.getSenders()
  );

  const submitSender = async () => {
    if (
      !senderDraft.name.trim() ||
      !senderDraft.country.trim() ||
      !senderDraft.address.trim() ||
      !senderDraft.registration_number.trim()
    ) {
      toast({
        title: 'Name, country, address and registration number are required',
        status: 'error',
      });
      return;
    }
    const payload = {
      name: senderDraft.name.trim(),
      country: senderDraft.country.trim().toUpperCase(),
      address: senderDraft.address.trim(),
      registration_number: senderDraft.registration_number.trim(),
      website_url: emptyToNull(senderDraft.website_url) ?? undefined,
      supported_sector_categories: csvToArrayOrNull(
        senderDraft.supported_sector_categories
      ),
      supported_beneficiary_countries: csvToArrayOrNull(
        senderDraft.supported_beneficiary_countries.toUpperCase()
      ),
      supported_bank_countries: csvToArrayOrNull(
        senderDraft.supported_bank_countries.toUpperCase()
      ),
      min_amount: emptyToNull(senderDraft.min_amount),
      max_amount: emptyToNull(senderDraft.max_amount),
      internal_notes: senderDraft.internal_notes.trim() || undefined,
    };
    try {
      setBusy(true);
      if (editingSenderId) {
        await invoicePaymentsService.updateSender(editingSenderId, payload);
        toast({ title: 'Sender updated', status: 'success' });
      } else {
        await invoicePaymentsService.createSender(payload);
        toast({ title: 'Sender created', status: 'success' });
      }
      setSenderFormOpen(false);
      setEditingSenderId(null);
      setSenderDraft(EMPTY_SENDER);
      await mutate();
    } catch (error) {
      adminService.errorHandler(error);
    } finally {
      setBusy(false);
    }
  };

  const toggleSenderStatus = async (sender: IInvoiceSender) => {
    try {
      setBusy(true);
      await invoicePaymentsService.updateSender(sender.id, {
        status:
          sender.status === EConfigStatus.ACTIVE
            ? EConfigStatus.INACTIVE
            : EConfigStatus.ACTIVE,
      });
      await mutate();
    } catch (error) {
      adminService.errorHandler(error);
    } finally {
      setBusy(false);
    }
  };

  const submitAccount = async () => {
    if (!accountFormSenderId) return;
    if (
      !accountDraft.currency.trim() ||
      !accountDraft.bank_name.trim() ||
      !accountDraft.bank_country.trim() ||
      !accountDraft.bank_address.trim() ||
      !accountDraft.supported_rails.trim()
    ) {
      toast({
        title:
          'Currency, bank name, bank country, bank address and rails are required',
        status: 'error',
      });
      return;
    }
    const payload = {
      currency: accountDraft.currency.trim().toUpperCase(),
      bank_name: accountDraft.bank_name.trim(),
      bank_country: accountDraft.bank_country.trim().toUpperCase(),
      bank_address: accountDraft.bank_address.trim(),
      iban: emptyToNull(accountDraft.iban),
      account_number: emptyToNull(accountDraft.account_number),
      sort_code: emptyToNull(accountDraft.sort_code),
      routing_number: emptyToNull(accountDraft.routing_number),
      bic_swift: emptyToNull(accountDraft.bic_swift),
      supported_rails: (csvToArrayOrNull(
        accountDraft.supported_rails.toUpperCase()
      ) ?? []) as string[],
    };
    try {
      setBusy(true);
      if (editingAccountId) {
        await invoicePaymentsService.updateSenderAccount(
          editingAccountId,
          payload
        );
        toast({ title: 'Account updated', status: 'success' });
      } else {
        await invoicePaymentsService.createSenderAccount(
          accountFormSenderId,
          payload
        );
        toast({ title: 'Account added', status: 'success' });
      }
      setAccountFormSenderId(null);
      setEditingAccountId(null);
      setAccountDraft(EMPTY_ACCOUNT);
      await mutate();
    } catch (error) {
      adminService.errorHandler(error);
    } finally {
      setBusy(false);
    }
  };

  const toggleAccountStatus = async (account: IInvoiceSenderAccount) => {
    try {
      setBusy(true);
      await invoicePaymentsService.updateSenderAccount(account.id, {
        status:
          account.status === EConfigStatus.ACTIVE
            ? EConfigStatus.INACTIVE
            : EConfigStatus.ACTIVE,
      });
      await mutate();
    } catch (error) {
      adminService.errorHandler(error);
    } finally {
      setBusy(false);
    }
  };

  const senderField = (
    key: keyof ISenderDraft,
    label: string,
    span2 = false
  ) => (
    <div className={span2 ? 'col-span-2' : ''}>
      <label className={labelCls}>{label}</label>
      <input
        className={inputCls}
        value={senderDraft[key]}
        onChange={e =>
          setSenderDraft({ ...senderDraft, [key]: e.target.value })
        }
      />
    </div>
  );

  const accountField = (key: keyof IAccountDraft, label: string) => (
    <div>
      <label className={labelCls}>{label}</label>
      <input
        className={inputCls}
        value={accountDraft[key]}
        onChange={e =>
          setAccountDraft({ ...accountDraft, [key]: e.target.value })
        }
      />
    </div>
  );

  return (
    <div>
      <CreatePermissionGate resource={PERMISSION_RESOURCES.INVOICE_CONFIG}>
        {!senderFormOpen && (
          <button
            className={`mb-4 ${primaryBtn}`}
            onClick={() => {
              setSenderFormOpen(true);
              setEditingSenderId(null);
              setSenderDraft(EMPTY_SENDER);
            }}
          >
            Add sender
          </button>
        )}
      </CreatePermissionGate>

      {senderFormOpen && (
        <div className="mb-4 rounded border border-gray-200 p-3 dark:border-white/20">
          <div className="mb-2 text-sm font-bold dark:text-white">
            {editingSenderId ? 'Edit sender' : 'New sender'}
          </div>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            {senderField('name', 'Legal name')}
            {senderField('country', 'Country (ISO-2)')}
            {senderField('registration_number', 'Registration No.')}
            {senderField('website_url', 'Website')}
            {senderField('address', 'Registered address', true)}
            {senderField('min_amount', 'Min amount')}
            {senderField('max_amount', 'Max amount')}
            {senderField(
              'supported_sector_categories',
              'Sector categories (comma, empty = all)',
              true
            )}
            {senderField(
              'supported_beneficiary_countries',
              'Beneficiary countries (ISO-2 comma, empty = all)',
              true
            )}
            {senderField(
              'supported_bank_countries',
              'Bank countries (ISO-2 comma, empty = all)',
              true
            )}
            {senderField('internal_notes', 'Internal notes', true)}
          </div>
          <div className="mt-3 flex gap-2">
            <button
              disabled={busy}
              onClick={submitSender}
              className={primaryBtn}
            >
              {editingSenderId ? 'Save sender' : 'Create sender'}
            </button>
            <button
              className={secondaryBtn}
              onClick={() => {
                setSenderFormOpen(false);
                setEditingSenderId(null);
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {senders?.map(sender => (
        <div
          key={sender.id}
          className="mb-4 rounded border border-gray-200 p-3 dark:border-white/20"
        >
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <span className="text-base font-bold dark:text-white">
                {sender.name}
              </span>{' '}
              <span
                className={`ml-2 rounded px-2 py-0.5 text-xs ${
                  sender.status === EConfigStatus.ACTIVE
                    ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
                    : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
                }`}
              >
                {sender.status}
              </span>
              <div className="text-xs text-gray-500">
                {sender.country} · Reg. {sender.registration_number} ·{' '}
                {sender.address}
              </div>
            </div>
            <UpdatePermissionGate
              resource={PERMISSION_RESOURCES.INVOICE_CONFIG}
            >
              <div className="flex gap-2">
                <button
                  className={smallBtn}
                  onClick={() => {
                    setSenderFormOpen(true);
                    setEditingSenderId(sender.id);
                    setSenderDraft(senderToDraft(sender));
                  }}
                >
                  Edit
                </button>
                <button
                  className={smallBtn}
                  disabled={busy}
                  onClick={() => toggleSenderStatus(sender)}
                >
                  {sender.status === EConfigStatus.ACTIVE
                    ? 'Deactivate'
                    : 'Activate'}
                </button>
                <button
                  className={smallBtn}
                  onClick={() => {
                    setAccountFormSenderId(sender.id);
                    setEditingAccountId(null);
                    setAccountDraft(EMPTY_ACCOUNT);
                  }}
                >
                  Add account
                </button>
              </div>
            </UpdatePermissionGate>
          </div>

          {(sender.supported_sector_categories ||
            sender.supported_beneficiary_countries ||
            sender.supported_bank_countries ||
            sender.min_amount ||
            sender.max_amount) && (
            <div className="mt-1 text-xs text-gray-500">
              {sender.supported_sector_categories && (
                <div>
                  Sectors: {sender.supported_sector_categories.join(', ')}
                </div>
              )}
              {sender.supported_beneficiary_countries && (
                <div>
                  Beneficiary countries:{' '}
                  {sender.supported_beneficiary_countries.join(', ')}
                </div>
              )}
              {sender.supported_bank_countries && (
                <div>
                  Bank countries: {sender.supported_bank_countries.join(', ')}
                </div>
              )}
              {(sender.min_amount || sender.max_amount) && (
                <div>
                  Amount range: {sender.min_amount ?? '0'} –{' '}
                  {sender.max_amount ?? '∞'}
                </div>
              )}
            </div>
          )}

          <div className="mt-2 overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr>
                  <th className={headerClass}>Currency</th>
                  <th className={headerClass}>Bank</th>
                  <th className={headerClass}>Country</th>
                  <th className={headerClass}>IBAN / Account</th>
                  <th className={headerClass}>BIC / Routing / Sort</th>
                  <th className={headerClass}>Rails</th>
                  <th className={headerClass}>Status</th>
                  <th className={headerClass}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {(sender.accounts ?? []).map(account => (
                  <tr key={account.id}>
                    <td className={cellClass}>{account.currency}</td>
                    <td className={cellClass}>{account.bank_name}</td>
                    <td className={cellClass}>{account.bank_country}</td>
                    <td className={`${cellClass} font-mono text-xs`}>
                      {account.iban ?? account.account_number ?? '—'}
                    </td>
                    <td className={`${cellClass} font-mono text-xs`}>
                      {[
                        account.bic_swift,
                        account.routing_number,
                        account.sort_code,
                      ]
                        .filter(Boolean)
                        .join(' / ') || '—'}
                    </td>
                    <td className={cellClass}>
                      {(account.supported_rails ?? []).join(', ')}
                    </td>
                    <td className={cellClass}>
                      <span
                        className={
                          account.status === EConfigStatus.ACTIVE
                            ? 'text-green-600'
                            : 'text-gray-400'
                        }
                      >
                        {account.status}
                      </span>
                    </td>
                    <td className={`${cellClass} flex gap-2`}>
                      <UpdatePermissionGate
                        resource={PERMISSION_RESOURCES.INVOICE_CONFIG}
                      >
                        <button
                          className={smallBtn}
                          onClick={() => {
                            setAccountFormSenderId(sender.id);
                            setEditingAccountId(account.id);
                            setAccountDraft(accountToDraft(account));
                          }}
                        >
                          Edit
                        </button>
                        <button
                          className={smallBtn}
                          disabled={busy}
                          onClick={() => toggleAccountStatus(account)}
                        >
                          {account.status === EConfigStatus.ACTIVE
                            ? 'Deactivate'
                            : 'Activate'}
                        </button>
                      </UpdatePermissionGate>
                    </td>
                  </tr>
                ))}
                {(sender.accounts ?? []).length === 0 && (
                  <tr>
                    <td
                      className="py-2 text-center text-sm text-gray-500"
                      colSpan={8}
                    >
                      No bank accounts — sender cannot be offered until an
                      active account exists.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {accountFormSenderId === sender.id && (
            <div className="mt-3 rounded border border-gray-200 p-3 dark:border-white/20">
              <div className="mb-2 text-sm font-bold dark:text-white">
                {editingAccountId ? 'Edit bank account' : 'New bank account'}
              </div>
              <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
                {accountField('currency', 'Currency (e.g. USD)')}
                {accountField('bank_name', 'Bank name')}
                {accountField('bank_country', 'Bank country (ISO-2)')}
                {accountField('bank_address', 'Bank address')}
                {accountField('iban', 'IBAN')}
                {accountField('account_number', 'Account number')}
                {accountField('sort_code', 'Sort code')}
                {accountField('routing_number', 'Routing No. (ABA)')}
                {accountField('bic_swift', 'BIC / SWIFT')}
                {accountField(
                  'supported_rails',
                  'Rails (comma: SEPA, SWIFT, ACH, ABA…)'
                )}
              </div>
              <div className="mt-3 flex gap-2">
                <button
                  disabled={busy}
                  onClick={submitAccount}
                  className={primaryBtn}
                >
                  {editingAccountId ? 'Save account' : 'Add account'}
                </button>
                <button
                  className={secondaryBtn}
                  onClick={() => {
                    setAccountFormSenderId(null);
                    setEditingAccountId(null);
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      ))}

      {senders && senders.length === 0 && (
        <div className="py-4 text-center text-sm text-gray-500">
          No senders configured.
        </div>
      )}
    </div>
  );
};
